import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Flex,
    Button,
    Stack,
    Menu,
    MenuItem,
    MenuList,
    MenuButton,
} from "@chakra-ui/react";
import {
    FaEdit,
    FaTrash
} from "react-icons/fa";
import { AddIcon, HamburgerIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import Loading from "components/Loading/Loading";
import SearchTable2 from "components/Tables/SearchTable2";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { getEmptyPageResponse, toTablePageableData } from "utils";
import { collectionsService } from "services/collectionsService";
import { CollectionsFilter } from "./CollectionsFilter";
import { IoIosArrowDown } from "react-icons/io";
import { ROUTES } from "constants/routes-path";
import { COLLECTIONS_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

const CollectionsList = () => {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(COLLECTIONS_LIST_URL_DEFAULTS);
    const [response, setResponse] = useState(undefined);
    const showToast = useCustomToast();
    const navigate = useNavigate();
    const dialogRef = useRef(undefined);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const columns = useMemo(() => [
        {
            Header: t("digital-collections.list.table.header.ait-number"),
            accessor: "numero_ait",
            id: "numero_ait"
        },
        {
            Header: t("digital-collections.list.table.header.type"),
            accessor: "placa",
        },
        {
            Header: t("digital-collections.list.table.header.plate"),
            accessor: "nome",
            id: "nome_beneficiario"
        },
        {
            Header: t("digital-collections.list.table.header.cnh"),
            accessor: "foto",
            disableSortBy: true,
        },
        {
            Header: t("digital-collections.list.table.header.agent-enrollment"),
            accessor: "matricula_agente",
            id: "matricula_agente"
        },
        {
            Header: t("digital-collections.list.table.header.agent-name"),
            accessor: "nome_agente",
            id: "nome_agente"
        },
        {
            Header: t("digital-collections.list.table.header.limit"),
            accessor: "limite",
            id: "limite"
        },
        {
            Header: t("digital-collections.list.table.header.status"),
            accessor: "situacao",
            id: "situacao"
        },
        {
            Header: t("digital-collections.list.table.header.date"),
            accessor: "data",
            id: "data_criacao"
        },
        {
            Header: t('digital-collections.list.table.header.operations'),
            disableSortBy: true,
            actions: [
                {
                    label: t('system.actions.edit'),
                    onClick: (row) => { console.log(row) },
                    icon: <FaEdit />
                }
            ]
        }
    ], []);

    const newDarv = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.digitalCollections + ROUTES.darvCreate);
    };

    const drvList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.digitalCollections + ROUTES.drvList);
    };

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? searchParams.orderBy ?? "data_criacao";
        params.orderDirection = params.orderDirection ?? searchParams.orderDirection ?? "desc";
        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await collectionsService.list(params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            //showToast({ title: t(err), status: "error" });
        }
    };

    const clearCollectionsFilters = () => {
        fetchData({
            page: 1,
            filters: {
                data_inicial: null,
                data_final: null,
                numero_ait: null,
                situacao: null,
                placa: null,
            },
        });
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Flex gap="5" direction="column" pt={{ base: "150px", sm: "125px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" direction={{ sm: "column", md: "row" }}>
                <Flex gap="5" w="100%" justify="flex-start" align="center" direction={{ sm: "column", md: "row" }}>
                    <Button
                        variant="list-primary"
                        leftIcon={<HamburgerIcon fontSize="sm" />}
                        alignSelf={{ sm: "flex-start", lg: undefined }}
                        onClick={drvList}
                    >
                        {t("digital-collections.actions.drv-list")}
                    </Button>
                    <Button
                        variant="list-primary"
                        leftIcon={<AddIcon fontSize="sm" />}
                        alignSelf={{ sm: "flex-start", lg: undefined }}
                        onClick={newDarv}
                    >
                        {t("digital-collections.actions.new-darv")}
                    </Button>
                </Flex>

                <CollectionsFilter
                    filterSnapshot={searchParams.filters}
                    onFilter={(filters) => fetchData({ filters })}
                />

            </Flex>
            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("digital-collections.title")} subtitle={t("digital-collections.description")} />
                <CardBody>
                    <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearCollectionsFilters} />
                    {response ? (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                tableData={response.content}
                                pageableData={toTablePageableData(response, { defaultField: "data_criacao", ...searchParams })}
                                onChange={fetchData}
                                disableSearch={true}
                            />

                        </Box>
                    ) : (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    )}
                </CardBody>
            </Card>
        </Flex>
    );

};

export default CollectionsList;